import { Card } from "@/components/ui/card";
import { storyElement } from "@/lib/story-element";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Swords, Shield, Wind, Sparkles, Skull, Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocale } from "@/hooks/useLocale";

interface Combatant {
  name: string;
  hp?: number;
  hp_max?: number;
  max_hp?: number;
  ac?: number;
  is_player?: boolean;
  conditions?: string[];
  dead?: boolean;
}

interface DeathSaves {
  successes: number;
  failures: number;
}

interface CombatPanelProps {
  combatants: Combatant[];
  onAction: (action: string) => void;
  disabled?: boolean;
  currentTurnPlayer?: string;
  deathSaves?: Record<string, DeathSaves>;
  playerHp?: number;
  playerMaxHp?: number;
}

function getActions(t: any) {
  return [
    { id: "attack", label: t.widget_combat_attack, icon: Swords, desc: t.widget_combat_attack_desc },
    { id: "defend", label: t.widget_combat_defend, icon: Shield, desc: t.widget_combat_defend_desc },
    { id: "flee", label: t.widget_combat_flee, icon: Wind, desc: t.widget_combat_flee_desc },
    { id: "special", label: t.widget_combat_special, icon: Sparkles, desc: t.widget_combat_special_desc },
  ];
}

function DeathSaveTracker({ saves, label }: { saves: DeathSaves; label: string }) {
  return (
    <div className="flex items-center gap-2 text-[10px]">
      <span className="text-muted-foreground">{label}:</span>
      <div className="flex gap-0.5">
        {[0, 1, 2].map(i => (
          <div key={`s${i}`} className={cn(
            "w-2.5 h-2.5 rounded-full border",
            i < saves.successes ? "bg-green-500 border-green-600" : "border-muted-foreground/40"
          )} />
        ))}
      </div>
      <span className="text-muted-foreground">/</span>
      <div className="flex gap-0.5">
        {[0, 1, 2].map(i => (
          <div key={`f${i}`} className={cn(
            "w-2.5 h-2.5 rounded-full border",
            i < saves.failures ? "bg-destructive border-destructive" : "border-muted-foreground/40"
          )} />
        ))}
      </div>
    </div>
  );
}

export function CombatPanel({ combatants, onAction, disabled, currentTurnPlayer, deathSaves }: CombatPanelProps) {
  const { t } = useLocale();
  const ACTIONS = getActions(t);
  if (combatants.length === 0) return null;

  return (
    <Card {...storyElement("combat-panel")} className="p-3 space-y-3 border-destructive/30 bg-destructive/5">
      <div className="flex items-center gap-2">
        <Swords className="w-3.5 h-3.5 text-destructive" />
        <span className="text-xs font-semibold text-destructive uppercase tracking-wider">Combat</span>
      </div>

      {/* Combatants */}
      <div className="space-y-1.5">
        {combatants.map((c, i) => {
          const maxHp = c.hp_max || c.max_hp || 100;
          const hpPercent = c.hp != null ? Math.min(100, (c.hp / maxHp) * 100) : null;
          const isDead = c.dead || (c.hp != null && c.hp <= 0);
          const isActive = currentTurnPlayer && c.name.toLowerCase() === currentTurnPlayer.toLowerCase();
          const playerDs = isDead && deathSaves?.[c.name.toLowerCase().replace(/\s+/g, "_")];

          return (
            <div key={i} className={cn(
              "flex flex-col gap-1 text-xs p-1.5 rounded transition-colors",
              isDead && "opacity-50",
              isActive && "bg-primary/10 ring-1 ring-primary/30",
              c.is_player && "font-medium",
            )}>
              <div className="flex items-center gap-2">
                {isDead && <Skull className="w-3 h-3 text-destructive shrink-0" />}
                <span className={cn(
                  "truncate flex-1",
                  isDead ? "line-through text-muted-foreground" : c.is_player ? "text-foreground" : "text-muted-foreground",
                )}>
                  {c.name}
                  {isActive && <span className="text-primary ml-1 text-[10px]">◀</span>}
                </span>
                {c.ac != null && (
                  <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                    <Shield className="w-2.5 h-2.5" />{c.ac}
                  </span>
                )}
                {hpPercent != null && (
                  <div className="w-20 flex items-center gap-1">
                    <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all duration-500",
                          hpPercent > 50 ? "bg-green-500" : hpPercent > 25 ? "bg-primary" : "bg-destructive"
                        )}
                        style={{ width: `${hpPercent}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-muted-foreground w-6 text-right">{c.hp}</span>
                  </div>
                )}
              </div>
              {/* Conditions */}
              {c.conditions && c.conditions.length > 0 && (
                <div className="flex flex-wrap gap-0.5 pl-5">
                  {c.conditions.map((cond, ci) => (
                    <Badge key={ci} variant="outline" className="text-[9px] h-4 px-1 capitalize">
                      {cond}
                    </Badge>
                  ))}
                </div>
              )}
              {/* Death saves */}
              {playerDs && (
                <div className="pl-5">
                  <DeathSaveTracker saves={playerDs as DeathSaves} label={t.widget_combat_death_saves} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-4 gap-1.5">
        {ACTIONS.map(a => (
          <Button
            key={a.id}
            variant="outline"
            size="sm"
            className="h-8 text-xs flex-col gap-0 p-1"
            disabled={disabled}
            onClick={() => onAction(`I ${a.id}!`)}
            title={a.desc}
          >
            <a.icon className="w-3 h-3" />
            <span className="text-[10px]">{a.label}</span>
          </Button>
        ))}
      </div>
    </Card>
  );
}
